import { DataTable } from '@app/components/data-table/data-table';
import { DataTableToolbar } from '@app/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import {
  FilterOptions,
  useServerFilterOptions,
} from '@app/components/data-table/hooks/use-server-filter-options';
import { DataTableProps } from '@app/components/data-table/types/data-table';
import useSimpleDialog from '@app/hooks/use-simple-dialog';
import {
  BusinessTravelActivitiesList,
  PaginatedBusinessTravelActivitiesListList,
} from '@shared/api/types';
import { Button } from '@shared/components/ui/button';
import { parseAsInteger, useQueryState } from 'nuqs';
import { useMemo, useState } from 'react';
import BusinessTravelEditForm from './business-travel-edit-form';
import { useBusinessTravelTableColumns } from './business-travel-table-columns';

interface BusinessTravelTableProps
  extends DataTableProps<PaginatedBusinessTravelActivitiesListList> {}

export default function BusinessTravelTable({
  data,
  isLoading,
  isPreviousData,
  isFetchedAfterMount,
  status,
}: BusinessTravelTableProps) {
  const [perPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [selectedActivityUuid, setSelectedActivityUuid] =
    useState<BusinessTravelActivitiesList['uuid']>();

  const {
    showSimpleDialog: showBusinessTravelDialog,
    SimpleDialogComponent: BusinessTravelDialog,
    closeSimpleDialog: closeBusinessTravelDialog,
  } = useSimpleDialog();

  const { results: activities, count } = data || {};
  const pageCount = count ? Math.ceil(count / (perPage || 10)) : 0;

  const { timePeriodsFilter, sitesFilter, fuelsFilter } =
    useServerFilterOptions(
      ['timePeriods', 'sites', 'fuels'] as const,
      data?.filters || {}
    );

  const useFilterOptions: FilterOptions<['timePeriods', 'sites', 'fuels']> =
    useMemo(
      () => ({
        timePeriods: timePeriodsFilter,
        sites: sitesFilter,
        fuels: fuelsFilter,
      }),
      [timePeriodsFilter, sitesFilter, fuelsFilter]
    );

  const { table, context } = useServerDataTable({
    data: activities || [],
    columns: useBusinessTravelTableColumns({ filters: useFilterOptions }),
    pageCount,
    getRowId: (row) => row.uuid,
    initialState: {
      columnVisibility: {
        uuid: false,
        period: false,
        includeSecr: false,
      },
    },
  });

  return (
    <>
      <DataTable
        table={table}
        context={context}
        className="bg-gray-00"
        rowClassName={() => 'cursor-pointer hover:bg-gray-50'}
        onRowClick={(row) => {
          setSelectedActivityUuid(row?.original.uuid);
          showBusinessTravelDialog();
        }}
        isLoading={(!isFetchedAfterMount && isPreviousData) || isLoading}
        tableLoading={!data && status === 'loading'}
      >
        <DataTableToolbar
          table={table}
          filterOrder={['site', 'fuel', 'period', 'methodology', 'includeSecr']}
        >
          <Button
            size="sm"
            onClick={() => {
              setSelectedActivityUuid(undefined);
              showBusinessTravelDialog();
            }}
          >
            Add data
          </Button>
        </DataTableToolbar>
      </DataTable>
      {BusinessTravelDialog({
        header: selectedActivityUuid
          ? 'Edit business travel data'
          : 'Add business travel data',
        contentClassName: 'max-w-3xl',
        content: (
          <BusinessTravelEditForm
            activityUuid={selectedActivityUuid}
            closeDialog={closeBusinessTravelDialog}
          />
        ),
      })}
    </>
  );
}
